import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  getUnviewedOrderUuids,
  markOrderUnviewed,
  markOrderViewed,
  clearUnviewedOrders
} from '@/services/mercure/unviewedOrdersStorage';
import { type IOrderListItem } from '@/types/IOrder';
import { OrderStatusesEnum } from '@/types/OrderStatusesEnum';

export interface IUnviewedOrdersState {
  /** uuid непросмотренных заказов — зеркало реестра в localStorage. */
  uuids: string[];
}

const initialState: IUnviewedOrdersState = {
  uuids: getUnviewedOrderUuids()
};

const unviewedOrders = createSlice({
  name: 'unviewedOrders',
  initialState,
  reducers: {
    markUnviewed(state, action: PayloadAction<string>) {
      const uuid = action.payload;
      markOrderUnviewed(uuid);

      if (!state.uuids.includes(uuid)) {
        state.uuids.push(uuid);
      }
    },
    markViewed(state, action: PayloadAction<string>) {
      const uuid = action.payload;
      markOrderViewed(uuid);
      state.uuids = state.uuids.filter((item) => item !== uuid);
    },
    clearUnviewed(state) {
      clearUnviewedOrders();
      state.uuids = [];
    },
    /** Ресинхронизация стейта с реестром (возврат на вкладку, событие storage). */
    syncUnviewed(state) {
      state.uuids = getUnviewedOrderUuids();
    },
    /**
     * Снимает пометку «непросмотрен» с заказов, чей актуальный статус в серверной
     * выдаче уже не «новый» (`created`). Сценарий: менеджер вернулся после
     * перерыва, заказы из реестра давно взяты в работу другим менеджером —
     * подсветка/счётчик для них больше не актуальны. Заказы из реестра, которых
     * нет в текущей выдаче, не трогаем (их статус неизвестен — добьёт TTL).
     */
    reconcileWithOrders(state, action: PayloadAction<IOrderListItem[]>) {
      const staleUuids = new Set(
        action.payload
          .filter((order) => order.status !== OrderStatusesEnum.NEW)
          .map((order) => order.uuid)
      );

      if (staleUuids.size === 0) {
        return;
      }

      state.uuids.forEach((uuid) => {
        if (staleUuids.has(uuid)) {
          markOrderViewed(uuid);
        }
      });
      state.uuids = state.uuids.filter((uuid) => !staleUuids.has(uuid));
    }
  }
});

export const {
  markUnviewed,
  markViewed,
  clearUnviewed,
  syncUnviewed,
  reconcileWithOrders
} = unviewedOrders.actions;

export default unviewedOrders.reducer;
